'use client'
import { FC, ReactNode } from 'react'
import { InfoLightIcon } from '@components/icons/light'
import { Tooltip, tooltipVariants } from '@components/ui/tooltip'
import { cn } from '@utils'
import { PercentageChangeTag } from '@components/shared/percentage-change-tag'
import { VariantProps } from 'class-variance-authority'
import isNil from 'lodash/isNil'
import { AreaLoader } from '@components/shared/AreaLoader.tsx'

interface SimpleStatProps {
  label: string | ReactNode
  value?: string | ReactNode
  valueUsdc?: string | ReactNode
  tooltipVariant?: VariantProps<typeof tooltipVariants>['variant']
  tooltip?: string | ReactNode
  className?: string
  labelClassName?: string
  valueClassName?: string
  emptyValueClassName?: string
  prefix?: string
  prefixClassName?: string
  iconClassName?: string
  rawPrefix?: string | ReactNode
  // suffix is used to display a suffix after the value (eg % sign), has no space before
  suffix?: string | ReactNode
  suffixClassName?: string
  // currency is used to display a currency symbol after the value, has a space before
  currency?: string
  emptyState?: string | ReactNode
  percentageChange?: number
  alignRight?: boolean
  isLoading?: boolean
  customLoader?: ReactNode
  textSize?: 'default' | 'small'
  tagVariant?: 'light' | 'dark' | 'filled'
  valueUsdcClassName?: string
  percentageChangeClassName?: string
  icon?: ReactNode
  equalValue?: string | undefined
}

export const SimpleStat: FC<SimpleStatProps> = ({
  className,
  icon,
  prefix,
  rawPrefix,
  suffix,
  tooltip,
  currency,
  label,
  value,
  valueUsdc,
  labelClassName = 'text-cw-midnight-purple/80',
  valueClassName = 'text-cw-midnight-purple text-h3',
  emptyValueClassName,
  percentageChange,
  percentageChangeClassName,
  emptyState = 'N/A',
  tooltipVariant = 'default',
  alignRight = false,
  prefixClassName = 'text-cw-midnight-purple',
  suffixClassName = 'text-cw-midnight-purple',
  iconClassName = 'text-cw-midnight-purple',
  isLoading,
  textSize = 'default',
  customLoader = <AreaLoader width="10rem" height="2rem" />,
  tagVariant = 'light',
  valueUsdcClassName = '',
  equalValue,
}) => {
  return (
    <div
      className={cn(
        '!relative border border-cw-purple-lighter rounded-2xl px-3 py-2 w-full font-funnel flex flex-col gap-1',
        className,
      )}
    >
      <div className={'flex gap-2 justify-between'}>
        <div
          className={cn(
            'text-sm text-left text-cw-midnight-purple uppercase font-montserrat font-medium',
            labelClassName,
          )}
        >
          {label}
        </div>

        {tooltip && (
          <Tooltip
            content={tooltip}
            variant={tooltipVariant}
            triggerProps={{ asChild: true }}
          >
            <button
              type="button"
              className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
              aria-label="More information"
              onClick={(e) => e.stopPropagation()}
            >
              <InfoLightIcon
                className={cn(
                  'h-3.5 w-3.5',
                  iconClassName ? iconClassName : 'text-cw-light-grey',
                )}
              />
            </button>
          </Tooltip>
        )}

        {!!percentageChange && !isLoading && (
          <div className="sm:hidden">
            <PercentageChangeTag
              className={cn(percentageChangeClassName, 'relative left-0 right-0')}
              variant={tagVariant}
              value={percentageChange}
            />
          </div>
        )}
      </div>

      <div
        className={cn(
          'flex gap-2 font-normal text-body-xl-bold xs:text-h3 items-center',
          {
            'justify-end': alignRight,
          },
        )}
      >
        {isLoading && customLoader}

        {!isNil(value) && !isLoading && (
          <>
            {icon && icon}
            {prefix && (
              <span
                className={cn('text-cw-midnight-purple font-funnel', prefixClassName)}
              >{`${prefix} `}</span>
            )}
            {rawPrefix && rawPrefix}
            <span
              className={cn('text-h3 font-funnel', valueClassName, {
                'font-medium': textSize === 'default',
                'font-normal text-lg': textSize === 'small',
              })}
            >
              {value}
            </span>
            {equalValue && (
              <div className="flex items-center gap-3 ml-1">
                <img
                  src="/images/wlth/arrow-left-right-round.webp"
                  width={16}
                  height={16}
                  alt="equal value"
                />
                <span
                  className={
                    'text-sm font-medium font-montserrat text-cw-midnight-purple/80'
                  }
                >
                  {equalValue}
                </span>
              </div>
            )}
            {suffix && (
              <span
                className={`text-cw-midnight-purple font-funnel -ml-2 ${suffixClassName}`}
              >
                {suffix}
              </span>
            )}
            {currency && <span className="text-cw-light-grey">{` ${currency}`}</span>}
          </>
        )}

        {isNil(value) && !isLoading && (
          <span
            className={cn(
              'font-bold text-h3 font-funnel',
              valueClassName,
              emptyValueClassName,
            )}
          >
            {emptyState}
          </span>
        )}

        {!!percentageChange && !isLoading && (
          <div className="hidden sm:flex ml-auto">
            <PercentageChangeTag
              className={cn(percentageChangeClassName, 'relative left-0 right-0')}
              variant={tagVariant}
              value={percentageChange}
            />
          </div>
        )}
      </div>
      {!isNil(valueUsdc) && (
        <div
          className={cn(
            valueUsdcClassName,
            'flex gap-2 font-normal text-body-xl-bold xs:text-h4 items-center',
            {
              'justify-end': alignRight,
            },
          )}
        >
          {isLoading && customLoader}

          {!isLoading && (
            <>
              {rawPrefix && rawPrefix}
              <span
                className={cn(valueClassName, 'ml-1.5 text-h3', {
                  'font-normal text-lg text-cw-electric-purple': textSize === 'default',
                  'font-normal text-lg': textSize === 'small',
                })}
              >
                $&nbsp;{valueUsdc}
              </span>
              {suffix && (
                <span className={`text-cw-light-grey -ml-2 ${suffixClassName}`}>
                  {suffix}
                </span>
              )}
              {currency && <span className="text-cw-light-grey">{` ${currency}`}</span>}
            </>
          )}

          {isNil(valueUsdc) && !isLoading && (
            <span
              className={cn('font-bold text-h3', valueClassName, emptyValueClassName)}
            >
              {emptyState}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
